import { useContext, useEffect, useMemo, useState, type FC } from 'react';
import InlineOptions from '../../inline-options';
import Chart from '../../chart';
import { VolSmileContext } from '../../../../providers/widgets';
import { SnapshotTypeEnum } from '../../../../model/snapshot_type';
import { lineChartColors } from '../../../../theme/colors';
import {
  VOL_SMILE_PERIOD_KEYS,
  VOL_SMILE_TENORS_WITH_ATMF_KEYS,
} from '../../../../providers/widgets/volatility-smile/vol-smile.constants';
import './styles.scss';
import TimestampInfo from '../../timestamp-info';
import usePersistentState from '../../../../providers/use-persistent-state';

type Chip = {
  key: string;
  color: string;
};

const flipKeys = (input: Record<string, Record<string, number>> = {} as any) => {
  const transformed: Record<string, Record<string, number>> = {};
  Object.keys(input || {}).forEach((primary) => {
    const inner = input[primary] || {};
    Object.keys(inner).forEach((secondary) => {
      if (!transformed[secondary]) transformed[secondary] = {};
      transformed[secondary][primary] = inner[secondary];
    });
  });
  return transformed;
};

const TermStructureContent: FC<{ providerKey: string }> = ({ providerKey }) => {
  const { type, setType, tenors, currentTimeStamp } = useContext(VolSmileContext);

  const days = useMemo(() => [1, 2, 3, 7, 15, 30, 60, 90, 180, 270, 365], []);
  const arrCategories = useMemo(() => Array.from({ length: 370 }, (_, i) => i + 1), []);

  const { tenorsByVol, dates, seriesKeys } = useMemo(() => {
    if (!tenors)
      return {
        tenorsByVol: undefined,
        dates: [] as string[],
        seriesKeys: [] as string[],
      };

    if (type === SnapshotTypeEnum.EE) {
      const flipped = flipKeys(tenors as any);
      const chips = VOL_SMILE_TENORS_WITH_ATMF_KEYS.filter((k) =>
        Object.prototype.hasOwnProperty.call(flipped, k),
      );
      const someKey = chips[0] || Object.keys(flipped)[0];
      const ds = someKey ? Object.keys(flipped[someKey] || {}) : [];
      const sortedDates = ds.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
      return {
        tenorsByVol: flipped,
        dates: sortedDates,
        seriesKeys: chips.length ? chips : Object.keys(flipped),
      };
    }

    const flipped = flipKeys(tenors as any);
    const chips = VOL_SMILE_TENORS_WITH_ATMF_KEYS.filter((k) =>
      Object.prototype.hasOwnProperty.call(flipped, k),
    );
    return {
      tenorsByVol: flipped,
      dates: VOL_SMILE_PERIOD_KEYS,
      seriesKeys: chips.length ? chips : Object.keys(flipped),
    };
  }, [tenors, type]);

  const [chips, setChips] = useState<Chip[]>([]);
  const [legendSelected, setLegendSelected] = usePersistentState<Record<string, boolean>>(
    [providerKey, 'TERM-STRUCTURE'],
    'legendSelected',
    {},
  );

  useEffect(() => {
    if (!seriesKeys || seriesKeys.length === 0) {
      setChips([]);
      return;
    }
    setChips((prev) => {
      const prevKeys = prev.map((c) => c.key);
      const sameLength = prevKeys.length === seriesKeys.length;
      const sameOrder = sameLength && prevKeys.every((k, i) => k === seriesKeys[i]);
      if (sameOrder) return prev;
      return seriesKeys.map((k, idx) => ({
        key: k,
        color: lineChartColors[idx % lineChartColors.length],
      }));
    });
  }, [seriesKeys]);

  useEffect(() => {
    if (!chips.length) {
      return;
    }

    setLegendSelected((prev) => {
      const next: Record<string, boolean> = {};
      chips.forEach((c) => {
        next[c.key] = prev.hasOwnProperty(c.key) ? prev[c.key] : true;
      });
      return next;
    });
  }, [chips]);

  const xAxis = useMemo(() => {
    if (type === SnapshotTypeEnum.EE) {
      const labelsFull = (dates || []).map((d) => {
        const dt = new Date(d);
        return Number.isNaN(dt.getTime()) ? d : dt.toISOString().slice(0, 10);
      });
      const labels = labelsFull.slice(0, days.length);
      return {
        type: 'category' as const,
        data: arrCategories,
        boundaryGap: false,
        min: (v: any) => (typeof v?.min === 'number' ? v.min - 1 : 0),
        max: (v: any) => (typeof v?.max === 'number' ? v.max + 1 : arrCategories.length + 1),
        axisLabel: {
          rotate: -30,
          hideOverlap: true,
          margin: 20,
          interval: (_index: number, value: string | number) =>
            days.includes(parseInt(String(value), 10)),
          formatter: (value: string | number) => {
            const idx = days.indexOf(parseInt(String(value), 10));
            if (idx < 0) return '';
            const label = labels[idx] || '';
            return `{pad|${label}}`;
          },
          rich: {
            pad: {
              padding: [0, 0, 0, 60],
            },
          },
        },
      };
    }
    return { type: 'category' as const, data: VOL_SMILE_PERIOD_KEYS };
  }, [type, dates, days, arrCategories]);

  const series = useMemo(() => {
    if (!tenorsByVol || chips.length === 0) return [] as any[];

    if (type === SnapshotTypeEnum.EE) {
      return chips.map((c) => {
        const limitedDates = dates.slice(0, days.length);
        const values = limitedDates.map((d, idx) => {
          const v = tenorsByVol?.[c.key]?.[d];
          return v == null ? null : [days[idx], v];
        });
        return {
          id: c.key,
          type: 'line' as const,
          name: c.key,
          color: c.color,
          data: values,
          symbolSize: 6,
          symbol: 'circle',
          animation: false,
          animationDuration: 0,
          lineStyle: { type: 'solid' },
        };
      });
    }

    return chips.map((c) => {
      const values = VOL_SMILE_PERIOD_KEYS.map((k) => {
        const v = tenorsByVol?.[c.key]?.[k];
        return v == null ? null : v;
      });
      return {
        id: c.key,
        type: 'line' as const,
        name: c.key,
        color: c.color,
        data: values,
        symbolSize: 6,
        symbol: 'circle',
        animation: false,
        animationDuration: 0,
        lineStyle: { type: 'solid' },
      };
    });
  }, [tenorsByVol, chips, dates, type, days]);

  const tooltip = useMemo(() => {
    if (type !== SnapshotTypeEnum.EE) {
      return {
        valueFormatter: (value: any) => {
          const num = typeof value === 'number' ? value : Number(value);
          if (value == null || Number.isNaN(num)) return '-';
          return `${(num * 100).toFixed(2)}%`;
        },
      } as any;
    }

    const labelsFull = (dates || []).map((d) => {
      const dt = new Date(d);
      return Number.isNaN(dt.getTime()) ? d : dt.toISOString().slice(0, 10);
    });
    const labels = labelsFull.slice(0, days.length);

    return {
      trigger: 'axis',

      formatter: (params: any[]) => {
        if (!params || !params.length) return '';
        const first = params[0];
        const xVal = Array.isArray(first?.value) ? first.value[0] : first?.axisValue;
        const idx = days.indexOf(parseInt(String(xVal), 10));
        const title = idx >= 0 ? labels[idx] || '' : '';

        const rows = params
          .filter((p) => p && p.value != null)
          .map((p) => {
            const color = p.color as string;
            const name = p.seriesName as string;
            const yVal = Array.isArray(p.value) ? p.value[1] : p.value;
            const num = typeof yVal === 'number' ? yVal : Number(yVal);
            const valueStr = Number.isNaN(num) ? '-' : `${(num * 100).toFixed(2)}%`;
            return `<div style="display:flex;gap:8px;align-items:center;margin:2px 0;">
                      <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${color}"></span>
                      <span style="min-width:48px">${name}</span>
                      <span>${valueStr}</span>
                    </div>`;
          })
          .join('');

        return `<div style="display:flex;flex-direction:column;gap:6px;">
                  <div style="font-weight:600;margin-bottom:4px;">${title}</div>
                  ${rows}
                </div>`;
      },
    } as any;
  }, [type, dates, days]);

  const yAxis = useMemo(
    () => ({
      type: 'value' as const,
      axisLabel: {
        formatter: (value: any) => `${(Number(value) * 100).toFixed(0)}%`,
      },
    }),
    [],
  );

  return (
    <div className="term-structure-content">
      <div className="term-structure-header">
        <InlineOptions
          options={[
            { value: SnapshotTypeEnum.EE, label: 'Expiry' },
            { value: SnapshotTypeEnum.CM, label: 'Tenor' },
          ]}
          selectedOption={type}
          onOptionChange={(val: string | string[]) =>
            setType((Array.isArray(val) ? val[0] : val) as SnapshotTypeEnum)
          }
        />
        <TimestampInfo timestamp={new Date(currentTimeStamp).getTime()} keepLocalTime />
      </div>

      <Chart
        series={series}
        xAxis={xAxis}
        yAxis={yAxis}
        merge={false}
        grid={{ bottom: type === SnapshotTypeEnum.EE ? '20px' : '60px' }}
        useBoundaryGap={false}
        legend={{
          selected: legendSelected,
        }}
        onLegendSelectChanged={(payload) => setLegendSelected(payload.selected || {})}
        tooltip={tooltip}
        className="term-structure-chart"
        isLoading={!tenors}
      />
    </div>
  );
};

export default TermStructureContent;
